import Phaser from 'phaser';
import { GAME_HEIGHT, GAME_WIDTH } from './config';
import { BootScene } from './scenes/BootScene';
import { PreloadScene } from './scenes/PreloadScene';
import { ForlagshusetScene } from './scenes/ForlagshusetScene';
import { HUDScene } from './scenes/HUDScene';
import { initStoreAsync } from './state/store';
import { registrerServiceWorker } from './pwa/registrerSw';
import { startInstallasjonsLytter } from './pwa/installasjon';
import { startNettstatusLytter } from './pwa/nettstatus';
import { audioManager } from './systems/audioManager';
import { ambientAudio } from './systems/ambientAudio';
import { ariaMirror } from './ui/AriaMirror';
import { hiddenActions } from './ui/HiddenActions';

const parent = document.getElementById('sp-canvas-host');
if (!parent) {
  throw new Error('Mangler #sp-canvas-host i index.html');
}

/**
 * Defensiv wrap — Phaser-spillet MÅ starte selv om et subsystem feiler.
 * Hvis ariaMirror/hiddenActions/audioManager kaster, fanger vi det
 * og logger så spillet ikke henger ved oppstart.
 */
function trygt(navn: string, fn: () => void): void {
  try {
    fn();
  } catch (err) {
    console.warn(`[start] ${navn} feilet under oppstart:`, err);
  }
}

/**
 * Vent på at custom-fonter (Playfair Display, DM Sans, Inter Tight) er
 * lastet før Phaser starter. Phaser måler tekst-bredde via canvas-API én
 * gang per `add.text(...)`, så hvis fontene ikke er lastet enda blir
 * tekstene målt med fallback-fonten (Georgia/Arial). Når Google Fonts
 * lastes senere, re-renderes ikke Phaser-tekstene — resultatet er at
 * tekst overlapper hverandre fordi posisjonene er beregnet for en helt
 * annen font-bredde. Maks 3 sek timeout så vi aldri henger.
 */
async function venterPaFonter(): Promise<void> {
  if (typeof document === 'undefined' || !document.fonts) return;
  try {
    // Tving last av de tre HUD/display/body-fontene eksplisitt slik at
    // `document.fonts.ready` faktisk venter på dem (den venter kun på
    // fonter som er "in use" i DOM, og Phaser-canvas teller ikke).
    const fontPromises: Promise<unknown>[] = [];
    if (typeof document.fonts.load === 'function') {
      fontPromises.push(
        document.fonts.load('700 88px "Playfair Display"'),
        document.fonts.load('700 52px "Playfair Display"'),
        document.fonts.load('500 20px "DM Sans"'),
        document.fonts.load('700 20px "DM Sans"'),
        document.fonts.load('600 16px "Inter Tight"'),
        document.fonts.load('700 13px "Inter Tight"'),
      );
    }
    fontPromises.push(document.fonts.ready);

    await Promise.race([
      Promise.all(fontPromises),
      new Promise<void>((resolve) => setTimeout(resolve, 3000)),
    ]);
  } catch (err) {
    console.warn('[fonts] venting feilet, fortsetter uten', err);
  }
}

async function start(): Promise<void> {
  // Save-lasting har 5s timeout internt; vi tar likevel try/catch som
  // ekstra sikring slik at Phaser alltid får starte.
  try {
    await Promise.all([initStoreAsync(), venterPaFonter()]);
  } catch (err) {
    console.warn('[start] initStoreAsync/fonter feilet, fortsetter med default-state', err);
  }

  new Phaser.Game({
    type: Phaser.AUTO,
    parent: parent!,
    transparent: true,
    width: GAME_WIDTH,
    height: GAME_HEIGHT,
    scale: {
      mode: Phaser.Scale.FIT,
      autoCenter: Phaser.Scale.CENTER_BOTH,
    },
    render: {
      pixelArt: false,
      antialias: true,
    },
    scene: [BootScene, PreloadScene, ForlagshusetScene, HUDScene],
  });

  trygt('registrerServiceWorker', () => registrerServiceWorker());
  trygt('startInstallasjonsLytter', () => startInstallasjonsLytter());
  trygt('startNettstatusLytter', () => startNettstatusLytter());
  trygt('audioManager.start', () => audioManager.start());
  trygt('ariaMirror.start', () => ariaMirror.start());
  trygt('hiddenActions.mount', () => hiddenActions.mount());

  // Aktiver ambient-loop ved første bruker-interaksjon (autoplay-policy).
  const aktiverAmbient = () => {
    try {
      ambientAudio.markerBrukerInteraksjon();
    } catch (err) {
      console.warn('[ambient] markerBrukerInteraksjon feilet', err);
    }
    window.removeEventListener('pointerdown', aktiverAmbient);
    window.removeEventListener('keydown', aktiverAmbient);
  };
  window.addEventListener('pointerdown', aktiverAmbient, { once: true });
  window.addEventListener('keydown', aktiverAmbient, { once: true });
}

void start().catch((err) => {
  console.error('[start] fatal feil under spilloppstart', err);
});
